import type {
  AllFormElementsJson,
  FormElementConditionJson,
  FormElementJson,
} from '../../../modules/edit/forms-conditional-logic';

export interface CheckBoxValuesJson {
  checked: string[],
  other?: string,
}

/** Gets rid of the index that forms prepends to the values of some discrete elements (e.g. "1-honk") */
function removeIndex(value: string): string {
  return value.replace(/^\d+-/, '');
}

export function isSatisfied(condition: FormElementConditionJson, allElementData: AllFormElementsJson, allElementValues: {[key: number]: string | CheckBoxValuesJson}) : boolean {
  switch (condition.type) {
    case 'and':
      return condition.conditions.every((c) => isSatisfied(c, allElementData, allElementValues));
    case 'or':
      return condition.conditions.find((c) => isSatisfied(c, allElementData, allElementValues)) !== undefined;
  }

  if (!condition.targetElementId) { return true; } // Ignore this one, it doesn't have a target

  // Having ruled out the two group conditions, we assume that this has an element ID attached to it.
  const element = allElementData.elements.find((element) => element.id === condition.targetElementId)

  if (!element) { return true; } // Ignore this one, the target may have been deleted at some point

  const value = allElementValues[element.id];

  // Special case for dates - anything not matching the format dd/MM/yyyy will be counted as blank
  if (element.type === 'dateTimeElement' && !formsDateRegex.test(value as string)) {
    return condition.type === 'isblank';
  }

  switch (condition.type) {
    case 'eq':
      if (typeof value === 'string') {
        if (element.type === 'listElement' || element.type === 'paymentElement') {
          // Values come out with prepended index like "1-value"
          return removeIndex(value) === removeIndex(condition.value);
        } else {
          return value === condition.value;
        }
      } else if (typeof value === 'object') { // Must be checkboxes/radios
        return (value as CheckBoxValuesJson).checked.map(v => removeIndex(v)).includes(removeIndex(condition.value));
      }
      break;
    case 'isblank':
      return checkIsBlank(element, value);
    case 'notblank':
      return !checkIsBlank(element, value);
    case 'contains':
      return (value as string).includes(condition.value);
    case 'isother':
      return checkIsOther(element, value);
    case 'greaterthan':
    case 'lessthan':
      return handleCompare(condition, element, value as string);
  }

  return true; // No idea what this is! Ignore it.
}

export function checkIsOther(element: FormElementJson, value: string | CheckBoxValuesJson): boolean {
  switch (element.type) {
    case 'checkboxElement':
    case 'radioElement':
      return ((value as CheckBoxValuesJson)?.other?.trim() ?? '') !== '';
    case 'paymentElement':
      // If it's missing the index prefix we know it's "other"
      return (value as string).trim() !== '' && !((value as string).includes('-'));
    default:
      return false;
  }
}

export function checkIsBlank(element: FormElementJson, value: string | CheckBoxValuesJson | null): boolean {
  if (!value) { return true; }
  if (typeof value === 'string') {
    if (element.type === 'radioElement' || element.type === 'checkboxElement') {
      throw new Error(`Radio or checkbox element values should match the CheckBoxValuesJson scheme. Got: "${value}"`)
    }
    if (element.type === 'listElement') {
      return  value === '----';
    }
    if (element.type === 'pairList') {
      return value === '1=----2=----';
    }
    if (element.type === 'paymentElement') {
      return value === '0-';
    }
    return value.trim() === '';
  } else {
   return (value as CheckBoxValuesJson).checked.length === 0 && ((value as CheckBoxValuesJson).other?.trim() ?? '') === '';
  }
}

export function handleCompare(condition: FormElementConditionJson, element: FormElementJson, value: string | null) {
  if (!value || value.trim() === '') return false;
  let conditionValue: number | Date, elementValue: number | Date;

  switch(element.type) {
    case 'numberElement':
      conditionValue = parseInt(condition.value, 10);
      elementValue = parseInt(value, 10);
      break;
    case 'dateTimeElement':
      conditionValue = formDateToJSDate(condition.value);
      elementValue = formDateToJSDate(value);
      break;
    case 'paymentElement':
      conditionValue = parseInt(condition.value, 10);
      elementValue = parseFloat(removeIndex(value));
      break;
    default:
      return true;
  }

  if (condition.type === 'greaterthan') {
    return elementValue > conditionValue
  } else if (condition.type === 'lessthan') {
    return elementValue < conditionValue;
  }

  throw new Error(`Expected a condition of type "greaterthan" or "lessthan". Got: ${condition.type}`);
}

// Not keen on this but all our historical data uses dd/MM/yyyy and not UTC.
const formsDateRegex = /^(\d{2})\/(\d{2})\/(\d{4})$/;

export function formDateToJSDate(formDate: string | null): Date | null {
  if (!formDate) return null;
  if (!formsDateRegex.test(formDate)) {
    throw new Error(`Expected a date string in format dd/MM/yyyy, got: "${formDate}"`);
  }
  const parsedDate = formsDateRegex.exec(formDate);
  return new Date(parseInt(parsedDate[3], 10), parseInt(parsedDate[2], 10) - 1, parseInt(parsedDate[1], 10));
}
